import { Navigate, Outlet, useLocation } from 'react-router-dom';
import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {  getApiService } from '../services/api/api.service';
import { storageService, StorageKeys } from '../services/storage.service';

export interface JwtPayload {
  userId: number;
  username: string;
  role?: string;
  exp: number;
  iat: number;
}

export const decodeJwt = (token: string): JwtPayload | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const useAuthLogic = () => {
  console.log('useAuthLogic initialized');

  const [user, setUser] = useState<string | null>(() => {
    const storedUser = storageService.getItem(StorageKeys.User);
    console.log('Initializing user state from localStorage:', storedUser);
    return storedUser;
  });

  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(() => {
    const storedRole = storageService.getItem(StorageKeys.Role);
    return storedRole === 'superadmin';
  });

  const [token, setToken] = useState<string | null>(() => {
    const storedToken = storageService.getItem(StorageKeys.Token);
    console.log(
      'Initializing token state from localStorage:',
      storedToken ? 'Token exists' : 'No token',
    );

    // Set the token in the API service
    if (storedToken) {
      getApiService().setAuthToken(storedToken);
    }

    return storedToken;
  });

  const [role, setRole] = useState<string | null>(() => {
    const storedRole = storageService.getItem(StorageKeys.Role);
    console.log('Initializing role state from localStorage:', storedRole);
    return storedRole;
  });

  useEffect(() => {
    console.log('useAuthLogic effect - Current state:', {
      user,
      role,
      token: token ? 'Token exists' : 'No token',
    });
  }, [user, role, token]);

  // Sets the authentication state in both context and localStorage
  const setAuthState = useCallback(
    (newApiToken: string | null, newUsername: string | null, newRole: string | null) => {
      if (newApiToken && newUsername && newRole) {
        storageService.setAuth(newApiToken, newUsername, newRole);
        // Set the token in the API service
        getApiService().setAuthToken(newApiToken);
        setToken(newApiToken);
        setUser(newUsername);
        setRole(newRole);
        setIsSuperAdmin(newRole === 'superadmin');
        console.log('Auth state updated by setAuthState:', { user: newUsername, role: newRole, token: 'Token exists' });
      } else {
        storageService.clearAppData();
        // Clear the token in the API service
        getApiService().setAuthToken(null);
        setToken(null);
        setUser(null);
        setRole(null);
        setIsSuperAdmin(false);
        console.log('Auth state cleared by setAuthState');
      }
    },
    [], // Dependencies: setToken, setUser, setRole are stable
  );

  // Log out the user
  const logout = useCallback(() => {
    setAuthState(null, null, null);
  }, [setAuthState]);

  return {
    isSuperAdmin,
    user,
    token,
    role,
    isAuthenticated: !!token,
    setAuthState,
    logout,
  };
};

export const AuthContext = createContext<ReturnType<typeof useAuthLogic> | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuthLogic();
  return (<AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthGuardParams {
  allowedRoles?: string[];
}

export const AuthGuard: React.FC<AuthGuardParams> = ({ allowedRoles }) => {
  const { user, role } = useAuthContext();
  const location = useLocation();

  if (!user) {
    // User not logged in, redirect to login page
    // Pass the current location so we can redirect back after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && role && !allowedRoles.includes(role)) {
    // User logged in but does not have the required role, redirect to Forbidden page
    return <Navigate to="/forbidden" replace />;
  }

  // User is authenticated (and authorized if roles were specified)
  return <Outlet />;
};
